// ViewingDetailDrawer — Tabbed view (Record / Log feedback) for a single
// viewing record. Matches the table-row → modal flow in PropertyCase's
// Viewings module. Saving feedback persists to PATCH /agent/viewings/{id}
// (the table rows now carry real viewing ids from the case-leads bundle)
// and fires onSaved so the underlying table re-hydrates.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateViewing, type ViewingUpdate } from "@/lib/proagentApi";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  Section,
  Select,
  Tab,
  TextArea,
  TextInput,
} from "./ModalShell";

export type ViewingRecord = {
  id?: string;
  buyer?: string;
  what?: string;
  date?: string;
  via?: string;
  source?: string;
  outcome?: string;
  interest?: "" | "Low" | "Medium" | "Medium-High" | "High";
  feedback?: string;
  followup?: string;
  themes?: string[];
  offer?: string;
  // ISO timestamps for the Record-tab timeline.
  scheduledAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

type Mode = "view" | "log";

type Feedback = {
  notes?: string;
  interest?: string;
  themes?: string[];
  offer?: string;
  followup?: string;
};

const INTEREST_OPTIONS = ["Low", "Medium", "Medium-High", "High"] as const;

const THEME_CHIPS: { label: string; tone: "pos" | "warn" }[] = [
  { label: "Location: positive", tone: "pos" },
  { label: "Layout works", tone: "pos" },
  { label: "View / floor: positive", tone: "pos" },
  { label: "Price: concern", tone: "warn" },
  { label: "Noise concern", tone: "warn" },
  { label: "Floor: too low", tone: "warn" },
  { label: "Tenancy: timeline risk", tone: "warn" },
  { label: "Renovation: needs work", tone: "warn" },
];

export function ViewingDetailDrawer({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: ViewingRecord | null;
  onClose: () => void;
  // Fired after a successful PATCH so the opener (the Viewings table)
  // can re-fetch and show the persisted feedback without a reload.
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("view");
  const [feedback, setFeedback] = useState<Feedback>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Pre-fill the form from the record on open so editing an existing
  // viewing shows its saved values (not a blank form) — keeps the
  // optimistic and post-refresh views symmetric.
  useEffect(() => {
    if (!open) return;
    setMode(record?.feedback ? "view" : "log");
    setFeedback({
      notes: record?.feedback ?? "",
      interest: record?.interest,
      themes: record?.themes ?? [],
      offer: record?.offer ?? "",
      followup: record?.followup ?? "",
    });
    setSaving(false);
    setSaveError(null);
  }, [open, record]);

  if (!record) return null;

  // Persist everything the LogMode form collected. All five fields map
  // onto the Viewing schema: notes → feedback, interest → interest,
  // themes → themes, offer → verbal_offer, followup → notes. Without a
  // real id we can't PATCH — fall back to just closing.
  async function handleSave() {
    if (!record?.id) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const patch: ViewingUpdate = {
        feedback: feedback.notes ?? "",
        interest: feedback.interest ?? "",
        themes: feedback.themes ?? [],
        verbal_offer: feedback.offer ?? "",
        notes: feedback.followup ?? "",
      };
      await updateViewing(record.id, patch);
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} width={760} label="Viewing detail">
      <ModalHeader
        eyebrow={t("agent.flows.viewing.eyebrow", { defaultValue: "Viewing" })}
        title={record.buyer || record.what || t("agent.flows.viewing.fallback", { defaultValue: "Viewing record" })}
        hint={`${record.date || ""} · ${record.via || record.source || "Direct"}`}
        onClose={onClose}
      />

      <div
        style={{
          padding: "0 26px",
          borderBottom: "1px solid var(--ag-rule)",
          display: "flex",
          gap: 4,
        }}
      >
        <Tab active={mode === "view"} onClick={() => setMode("view")}>
          {t("agent.flows.viewing.tabRecord", { defaultValue: "Record" })}
        </Tab>
        <Tab active={mode === "log"} onClick={() => setMode("log")}>
          {record.feedback
            ? t("agent.flows.viewing.tabEdit", { defaultValue: "Edit feedback" })
            : t("agent.flows.viewing.tabLog", { defaultValue: "Log feedback" })}
        </Tab>
      </div>

      <ModalBody style={{ display: "block" }}>
        {mode === "view" && <ViewMode record={record} />}
        {mode === "log" && (
          <LogMode record={record} feedback={feedback} setFeedback={setFeedback} />
        )}
      </ModalBody>

      <ModalFooter
        leftHint={
          mode === "log"
            ? saveError
              ? <span style={{ color: "var(--ag-warn)" }}>{saveError}</span>
              : t("agent.flows.viewing.logHint", {
                  defaultValue: "AI will summarize across all viewings.",
                })
            : null
        }
      >
        {mode === "view" && (
          <>
            <Btn ghost onClick={onClose}>
              {t("agent.flows.close", { defaultValue: "Close" })}
            </Btn>
            <Btn onClick={() => setMode("log")}>
              {record.feedback
                ? t("agent.flows.viewing.update", { defaultValue: "Update feedback" })
                : t("agent.flows.viewing.log", { defaultValue: "Log feedback" })}
            </Btn>
          </>
        )}
        {mode === "log" && (
          <>
            <Btn ghost onClick={() => setMode("view")} disabled={saving}>
              {t("agent.flows.cancel", { defaultValue: "Cancel" })}
            </Btn>
            <Btn primary onClick={handleSave} disabled={saving}>
              {saving
                ? t("agent.flows.viewing.saving", { defaultValue: "Saving…" })
                : t("agent.flows.viewing.save", { defaultValue: "Save feedback" })}
            </Btn>
          </>
        )}
      </ModalFooter>
    </ModalShell>
  );
}

function fmtTimelineDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-SG", { day: "numeric", month: "short" });
}

function ViewMode({ record }: { record: ViewingRecord }) {
  const { t } = useTranslation();
  const interest = record.interest;
  const interestPct =
    interest === "High" ? 0.85 :
    interest === "Medium-High" ? 0.65 :
    interest === "Medium" ? 0.5 :
    interest === "Low" ? 0.3 : 0;
  const themes = record.themes ?? [];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Section title={t("agent.flows.viewing.feedback", { defaultValue: "Feedback" })}>
          {record.feedback ? (
            <div
              style={{
                fontFamily: "var(--ag-font-serif)",
                fontSize: 15,
                lineHeight: 1.55,
                color: "var(--ag-ink-2)",
              }}
            >
              "{record.feedback}"
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "var(--ag-ink-3)", fontStyle: "italic" }}>
              {t("agent.flows.viewing.noFeedback", {
                defaultValue: "No feedback logged yet. Click \"Log feedback\" to capture impressions.",
              })}
            </div>
          )}
        </Section>

        <Section
          title={t("agent.flows.viewing.themes", { defaultValue: "Themes" })}
          hint={t("agent.flows.viewing.themesHint", { defaultValue: "Tagged from feedback" })}
        >
          {themes.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "var(--ag-ink-3)", fontStyle: "italic" }}>
              {t("agent.flows.viewing.noThemes", { defaultValue: "No themes tagged yet." })}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {themes.map((label) => {
                const tone = /positive|works|likes/i.test(label)
                  ? "pos"
                  : /concern|negative|noise|high/i.test(label)
                    ? "warn"
                    : "neutral";
                const bg =
                  tone === "pos"
                    ? "var(--ag-pos-bg)"
                    : tone === "warn"
                      ? "var(--ag-warn-bg)"
                      : "var(--ag-surface-2)";
                const ink =
                  tone === "pos" ? "var(--ag-pos)" : tone === "warn" ? "var(--ag-warn)" : "var(--ag-ink-2)";
                return (
                  <span
                    key={label}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: bg,
                      color: ink,
                      fontSize: 11.5,
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </Section>

        <Section title={t("agent.flows.viewing.followup", { defaultValue: "Follow-up" })}>
          <div style={{ fontSize: 13, color: "var(--ag-ink-2)" }}>
            {record.followup || t("agent.flows.viewing.notSet", { defaultValue: "Not set" })}
          </div>
        </Section>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            padding: 14,
            background: "var(--ag-surface)",
            borderRadius: 10,
            border: "1px solid var(--ag-rule)",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--ag-ink-3)",
              marginBottom: 8,
            }}
          >
            {t("agent.flows.viewing.interest", { defaultValue: "Interest level" })}
          </div>
          <div
            style={{
              fontFamily: "var(--ag-font-serif)",
              fontSize: 22,
              fontWeight: 500,
              marginBottom: 8,
              letterSpacing: -0.2,
              color: interest ? "var(--ag-ink)" : "var(--ag-ink-3)",
            }}
          >
            {interest || t("agent.flows.viewing.notLogged", { defaultValue: "Not logged" })}
          </div>
          <div
            style={{ height: 6, background: "var(--ag-surface-2)", borderRadius: 999, overflow: "hidden" }}
          >
            <div
              style={{
                width: `${interestPct * 100}%`,
                height: "100%",
                background:
                  interestPct > 0.7
                    ? "var(--ag-pos)"
                    : interestPct > 0.45
                      ? "#8a7440"
                      : "#a0635a",
              }}
            />
          </div>
        </div>

        {record.offer && record.offer !== "—" && (
          <div
            style={{
              padding: 14,
              background: "var(--ag-ai-bg)",
              borderRadius: 10,
              border: "1px solid var(--ag-ai-rule)",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "var(--ag-ai-accent)",
                marginBottom: 6,
              }}
            >
              {t("agent.flows.viewing.verbalOffer", { defaultValue: "Verbal offer" })}
            </div>
            <div style={{ fontFamily: "var(--ag-font-mono)", fontSize: 16, fontWeight: 500 }}>
              {record.offer}
            </div>
          </div>
        )}

        <div
          style={{
            padding: 14,
            background: "var(--ag-surface)",
            borderRadius: 10,
            border: "1px solid var(--ag-rule)",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--ag-ink-3)",
              marginBottom: 8,
            }}
          >
            {t("agent.flows.viewing.timeline", { defaultValue: "Timeline" })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
            {[
              {
                t: t("agent.flows.viewing.tlCreated", { defaultValue: "Created" }),
                d: fmtTimelineDate(record.createdAt),
                dim: !record.createdAt,
              },
              {
                t: t("agent.flows.viewing.tlScheduled", { defaultValue: "Scheduled for" }),
                d: fmtTimelineDate(record.scheduledAt),
                dim: !record.scheduledAt,
              },
              {
                t: t("agent.flows.viewing.tlLogged", { defaultValue: "Feedback logged" }),
                d: record.feedback ? fmtTimelineDate(record.updatedAt) : "—",
                dim: !record.feedback,
              },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", opacity: s.dim ? 0.5 : 1 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: s.dim ? "var(--ag-ink-4)" : "var(--ag-accent)",
                  }}
                />
                <span style={{ flex: 1 }}>{s.t}</span>
                <span style={{ fontFamily: "var(--ag-font-mono)", color: "var(--ag-ink-3)", fontSize: 11 }}>
                  {s.d}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LogMode({
  record,
  feedback,
  setFeedback,
}: {
  record: ViewingRecord;
  feedback: Feedback;
  setFeedback: (updater: (f: Feedback) => Feedback) => void;
}) {
  const { t } = useTranslation();
  const update = <K extends keyof Feedback>(k: K, v: Feedback[K]) =>
    setFeedback((f) => ({ ...f, [k]: v }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field
        label={t("agent.flows.viewing.logBody", { defaultValue: "What did the buyer say?" })}
        hint={t("agent.flows.viewing.logBodyHint", {
          defaultValue: "One paragraph, your words. We'll extract themes.",
        })}
      >
        <TextArea
          value={feedback.notes ?? record.feedback ?? ""}
          onChange={(v) => update("notes", v)}
          rows={4}
          placeholder="e.g. Likes location and layout. Price feels high — would consider seriously at $1.43–1.45M."
        />
      </Field>

      <Field label={t("agent.flows.viewing.interest", { defaultValue: "Interest level" })}>
        <div style={{ display: "flex", gap: 6 }}>
          {INTEREST_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              active={(feedback.interest || record.interest) === opt}
              onClick={() => update("interest", opt)}
            >
              {opt}
            </Chip>
          ))}
        </div>
      </Field>

      <Field
        label={t("agent.flows.viewing.themes", { defaultValue: "Themes" })}
        hint={t("agent.flows.viewing.themesPick", {
          defaultValue: "Tap any that apply. AI will add more from your notes.",
        })}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {THEME_CHIPS.map((c) => {
            const selected = (feedback.themes || []).includes(c.label);
            const bg = selected
              ? c.tone === "pos"
                ? "var(--ag-pos-bg)"
                : "var(--ag-warn-bg)"
              : "var(--ag-surface)";
            const ink = selected
              ? c.tone === "pos"
                ? "var(--ag-pos)"
                : "var(--ag-warn)"
              : "var(--ag-ink-2)";
            const border = selected
              ? c.tone === "pos"
                ? "var(--ag-pos)"
                : "var(--ag-warn)"
              : "var(--ag-rule-strong)";
            return (
              <button
                key={c.label}
                onClick={() => {
                  const cur = feedback.themes || [];
                  update(
                    "themes",
                    selected ? cur.filter((x) => x !== c.label) : [...cur, c.label],
                  );
                }}
                style={{
                  padding: "6px 11px",
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 600,
                  background: bg,
                  color: ink,
                  border: `1px solid ${border}`,
                  cursor: "pointer",
                  fontFamily: "var(--ag-font-ui)",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field
          label={t("agent.flows.viewing.verbalOfferOptional", {
            defaultValue: "Verbal offer (if any)",
          })}
        >
          <TextInput
            mono
            value={feedback.offer}
            onChange={(v) => update("offer", v)}
            placeholder="$1.43M"
          />
        </Field>
        <Field label={t("agent.flows.viewing.followup", { defaultValue: "Follow-up" })}>
          <Select
            value={feedback.followup}
            onChange={(v) => update("followup", v)}
            options={[
              { value: "Chase decision in 48h", label: "Chase decision in 48h" },
              { value: "Follow up Friday", label: "Follow up Friday" },
              { value: "Send comparable unit", label: "Send comparable unit" },
              { value: "Convey to seller", label: "Convey to seller" },
              { value: "No follow-up — passed", label: "No follow-up — passed" },
            ]}
          />
        </Field>
      </div>

      <AIHint>
        {t("agent.flows.viewing.logAI", {
          defaultValue:
            "After saving, this feedback is added to the case's theme summary, the seller report draft updates automatically, and the buyer's interest score recalculates.",
        })}
      </AIHint>
    </div>
  );
}
